"use client";

import { CarouselItem } from "@components/ui/Carousel";
import ProductCard from "@components/ProductCard";
import { useEffect, useState } from "react";
import type { Product } from "types";

import ProductListCarousel from "./ProductListCarousel";
import ProductListCarouselSkeleton from "./ProductListCarouselSkeleton";
import ProductListMoreLink from "./ProductListMoreLink";

type ProductListCarouselSlotProps = {
  marketCode: string;
  productKeys: string[];
  count?: number | null;
  heading?: string | null;
  linkSlug?: string | null;
  linkText?: string | null;
};

export default function ProductListCarouselSlot({
  marketCode,
  productKeys,
  count,
  heading,
  linkSlug,
  linkText,
}: ProductListCarouselSlotProps) {
  const [products, setProducts] = useState<Product[] | null>(null);
  const productKeyList = JSON.stringify(productKeys.filter(Boolean));

  useEffect(() => {
    const keys = JSON.parse(productKeyList) as string[];
    if (!keys.length) {
      setProducts([]);
      return;
    }

    let cancelled = false;

    import("./loadProductListCarousel")
      .then((mod) =>
        mod.loadProductListCarousel({
          marketCode,
          productKeys: keys,
          count,
        }),
      )
      .then((result) => {
        if (!cancelled) setProducts(result);
      })
      .catch(() => {
        if (!cancelled) setProducts([]);
      });

    return () => {
      cancelled = true;
    };
  }, [marketCode, productKeyList, count]);

  if (!products) return <ProductListCarouselSkeleton />;
  if (!products.length) return null;

  return (
    <ProductListCarousel
      title={heading ?? undefined}
      link={
        <ProductListMoreLink slug={linkSlug ?? undefined}>
          {linkText}
        </ProductListMoreLink>
      }
    >
      {products.map((product, index) => (
        <CarouselItem
          key={`${product.id}-${index}`}
          className="basis-[75%] md:basis-[40%] lg:basis-1/4 xl:basis-1/5"
        >
          <ProductCard.Root product={product}>
            <ProductCard.Link>
              <ProductCard.Image>
                <ProductCard.Badges />
              </ProductCard.Image>
              <ProductCard.Content />
            </ProductCard.Link>
          </ProductCard.Root>
        </CarouselItem>
      ))}
    </ProductListCarousel>
  );
}
